// vpn-web-bo/src/pages/promo-codes/BatchGenerateModal.tsx
import { useEffect } from 'react';
import { Modal, Form as AntForm, Input, InputNumber, App } from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import type { SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authFetch } from '../../utils/api';
import {
  FormField,
  FormSection,
  FormActions,
  TargetBrandField,
} from '../../shared/form';
import {
  BatchGenerate,
  type BatchGenerateValues,
} from '../../schemas/promoCode';
import {
  ValidityMinutesField,
  PerDeviceRedeemLimitField,
  AllowedNodeTiersField,
  useNodeTierOptions,
} from './promoFields';

interface Props {
  open: boolean;
  /**
   * 去品牌化 M4：目前檢視品牌，僅用於開啟時預填「目標品牌」欄位（檢視=單品牌 →
   * 顯式帶入可再改；檢視=all → null 留空強制手選）。實際寫入品牌一律取表單顯式
   * 選擇的 targetBrand 欄位，絕不繼承檢視範圍 / activeBrand。
   */
  viewBrand: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DEFAULTS: BatchGenerateValues = {
  targetBrand: '',
  count: 50,
  prefix: 'WD-',
  name: '',
  campaign: null,
  grantMb: 1024,
  validitySeconds: 86400,
  perDeviceCapMb: 3072,
  perDeviceRedeemLimit: 0,
  allowedNodeTiers: null,
  totalRedemptionLimit: 0,
};

/**
 * 批量產碼 Modal — 鏡像 PromoCodeFormModal 的 RHF + zod + shared/form 寫法。
 * BO 強制：不可用 AntD `Form.useForm`+`rules`，改用 `react-hook-form` +
 * `zodResolver` + `<FormProvider>` + `<FormField control={<Input/>}>`。
 */
export const BatchGenerateModal: React.FC<Props> = ({
  open,
  viewBrand,
  onClose,
  onSuccess,
}) => {
  // VPN-81 B5 — 節點等級 ACL 建議選項（distinct node.minTier），開 modal 時抓一次。
  const tierOptions = useNodeTierOptions(open);
  // React 19 下 antd 靜態 `message.*` 走 legacy ReactDOM.render 不渲染（同
  // PromoCodeFormModal 的修法）→ 改用 App context 綁定的 message。
  const { message } = App.useApp();
  const form = useForm<BatchGenerateValues>({
    resolver: zodResolver(BatchGenerate),
    mode: 'onBlur',
    defaultValues: DEFAULTS,
  });

  // 開啟時重設：檢視=單品牌 → 預填該品牌（顯式可改）；檢視=all → 留空強制手選。
  useEffect(() => {
    if (open) {
      form.reset({ ...DEFAULTS, targetBrand: viewBrand ?? '' });
    }
  }, [open, viewBrand, form]);

  const onSubmit: SubmitHandler<BatchGenerateValues> = async (values) => {
    // M4：批量產碼顯式帶目標品牌（表單 TargetBrandField 選擇），絕不繼承檢視範圍 /
    // activeBrand。zod 已擋空值；此處 fail-closed 再擋一次。
    if (!values.targetBrand) {
      message.error('請選擇目標品牌後再批量產碼。');
      return;
    }
    const res = await authFetch('/api/admin/promo-codes/batch', {
      method: 'POST',
      headers: { 'X-Target-Brand': values.targetBrand },
      body: JSON.stringify(values),
    });
    const result = await res.json();
    if (result.code === 0) {
      message.success(result.msg || '已產生');
      form.reset(DEFAULTS);
      onSuccess();
    } else {
      message.error(result.msg || '產碼失敗');
    }
  };

  /** zod 驗證失敗時的可見回饋（對齊 PromoCodeFormModal，避免點按鈕靜默無反應）。 */
  const onInvalid: SubmitErrorHandler<BatchGenerateValues> = (errors) => {
    const keys = Object.keys(errors);
    if (keys.length === 0) return;
    message.warning(`有 ${keys.length} 個欄位待修正，請檢查表單紅字提示。`);
  };

  return (
    <Modal
      title="批量產碼"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={600}
    >
      <FormProvider {...form}>
        <AntForm layout="vertical" onFinish={form.handleSubmit(onSubmit, onInvalid)}>
          <FormSection title="產碼設定" description="一次產生多組唯一兌換碼">
            {/* M4：顯式選目標品牌（聚合檢視強制手選）。 */}
            <TargetBrandField<BatchGenerateValues> name="targetBrand" />
            <FormField<BatchGenerateValues>
              name="count"
              label="產生數量 (1-1000)"
              required
              control={
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              }
            />
            <FormField<BatchGenerateValues>
              name="prefix"
              label="前綴"
              help="產碼會在前綴後接隨機字元，例如 WD-A1B2C3D4"
              control={<Input placeholder="例如：WD-" />}
            />
            <FormField<BatchGenerateValues>
              name="name"
              label="名稱"
              control={<Input placeholder="後台辨識用，例如：五一活動" />}
            />
            <FormField<BatchGenerateValues>
              name="campaign"
              label="活動代號"
              control={<Input placeholder="選填" />}
            />
          </FormSection>

          <FormSection title="發放設定" description="每組兌換碼共用的發放設定">
            <FormField<BatchGenerateValues>
              name="grantMb"
              label="發放流量 (MB)"
              required
              control={<InputNumber min={0} style={{ width: '100%' }} />}
            />
            <ValidityMinutesField />
            <FormField<BatchGenerateValues>
              name="perDeviceCapMb"
              label="單裝置累計上限 (MB)"
              help="0 代表不限制"
              control={<InputNumber min={0} style={{ width: '100%' }} />}
            />
            <PerDeviceRedeemLimitField />
            <FormField<BatchGenerateValues>
              name="totalRedemptionLimit"
              label="每碼總兌換上限"
              help="0 代表不限制"
              control={
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="0 = 不限"
                />
              }
            />
          </FormSection>

          <FormSection
            title="使用範圍"
            description="限制這批兌換碼發放的權益可用於哪些節點等級"
          >
            <AllowedNodeTiersField tierOptions={tierOptions} />
          </FormSection>

          <FormActions
            onCancel={onClose}
            submitting={form.formState.isSubmitting}
            dirty={form.formState.isDirty}
            submitLabel="產生兌換碼"
          />
        </AntForm>
      </FormProvider>
    </Modal>
  );
};

export default BatchGenerateModal;
